import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";

import FactureModal from '../components/FactureModal';
import OperationModal from '../components/OperationModal';
import RecurrenceModal from '../components/RecurrenceModal';
import RecurrenceMonthsModal from '../components/RecurrenceMonthsModal';

// Import des hooks et constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { useNotification } from '../contexts/NotificationContext';
import { THEMES, THEME_COLORS, PAYMENTS_MOD } from "../components/Constants";

import { authFetch } from "../utils/api";

import { saveRecurrence, processRecurrences } from '../services/recurrenceService'
import { fetchTransactionById } from '../services/transactionService';

// ===============================================
// 🎨 PALETTE DE COULEURS (Nettoyage des constantes locales)
// ===============================================
// Suppression des constantes locales (elles viennent maintenant de themeColors)
// COLOR_PRIMARY, COLOR_SECONDARY, COLOR_SUCCESS, COLOR_TEXT_DARK, COLOR_BG_LIGHT, COLOR_NEUTRAL_BUTTON

// Fonction utilitaire pour formater la devise (à placer en dehors du composant)
const formatCurrency = (amount) => {
  const numberAmount = Number(amount);
  if (isNaN(numberAmount)) return "0.00";
  return numberAmount.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).replace('€', '').trim();
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "90vh",
    padding: "15px 20px 20px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0px", 
  },
  headerButtons: {
    display: "flex",
    gap: "10px", 
  },
  // ✅ STRUCTURE PRINCIPALE : Ligne flex
  mainContent: {
    display: "flex",
    flex: 1,
    gap: "20px", 
    alignItems: 'stretch', 
    maxHeight: 'calc(100vh - 200px)',
    overflow: 'hidden',
  },
  
  // ✅ COLONNE 1: Liste des récurrences
  recurringListSection: { 
    flex: 1, 
    padding: "15px", 
    borderRadius: "8px", 
    display: "flex", 
    flexDirection: "column", 
    overflowY: "auto", 
    boxShadow: '4px 4px 8px rgba(0,0,0,0.1)',
    minWidth: '300px',
    flexShrink: 0,
    scrollbarWidth: 'thin',
  }, 
  
  // ✅ COLONNE 2: Formulaire de modification/suppression
  leftPanel: { 
    flex: 1, 
    padding: "0px", 
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column", 
    overflowY: 'hidden',
    boxShadow: '4px 4px 8px rgba(0,0,0,0.1)',
    minWidth: '350px',
    flexShrink: 0,
  },

  formScrollArea: {
    padding: "15px 15px 0px 15px",
    overflowY: "auto",
    flex: 1,
    scrollbarWidth: 'thin',
  },

  // 💡 NOUVEAU : La barre de boutons fixe en bas
  stickyActions: {
    padding: "10px 15px 15px 15px",
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    borderRadius: "0 0 8px 8px", // Arrondi du bas
  },
  
  // ✅ COLONNE 3: Liste des mois
  monthListSection: { 
    flex: 1, 
    padding: "15px", 
    borderRadius: "8px",
    overflowY: 'auto', 
    boxShadow: '4px 4px 8px rgba(0,0,0,0.1)',
    minWidth: '300px',
    flexShrink: 0,
    scrollbarWidth: 'thin',
  },
  
  button: {
    padding: "10px 15px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "background-color 0.2s",
    outline: 'none',
  },
  // Les styles de bouton individuels seront appliqués dynamiquement
  primaryButton: {}, 
  secondaryButton: {}, 
  saveButton: {},
  deleteButton: {},
  
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  input: {
    padding: "10px", 
    borderRadius: "4px",
    // border et color sont gérés dynamiquement
    outline: 'none',
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "15px",
    // backgroundColor est géré dynamiquement
  },
  tableRow: {
    cursor: "pointer",
    // :hover est difficile à gérer en style inline, nous le ferons sur la prop
  },
  tableCell: {
    padding: "10px",
    // borderBottom et color sont gérés dynamiquement
    textAlign: "left",
  },
  formLabel: {
    fontWeight: 'bold',
    marginTop: '5px',
    // color est géré dynamiquement
  },
  title: {
    // color est géré dynamiquement
    marginBottom: '15px',
    marginTop: '0px',
  }
};


export default function RecurrencePage({
  currentUser,
  categories,
  accounts = [],
  persons,
  refreshDashboardData,
  refreshAllData,
  selectedPerson,
  selectedAccount
}) {
    // Récupération du thème (Hook en haut !)
    const { currentTheme } = useTheme();
    const { notify } = useNotification();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    // Définition des styles dynamiques pour les boutons (pour la lisibilité dans le rendu)
    const primaryButtonDynamic = { 
        backgroundColor: themeColors.secondary, 
        color: themeColors.textSecondary 
    };
    const secondaryButtonDynamic = { 
        backgroundColor: themeColors.defaultButton, 
        color: themeColors.textSecondary 
    };
    const saveButtonDynamic = { 
        backgroundColor: themeColors.success, 
        color: themeColors.textSecondary 
    };
    const deleteButtonDynamic = { 
        backgroundColor: themeColors.danger, 
        color: themeColors.textSecondary 
    };
    //const tableHeaderBg = themeColors.navActive;
    const hoverColor = themeColors.navActive; // Couleur de survol/sélection
    const borderCol = themeColors.inputBorder;
    const columnBg = themeColors.mainBackground;

    const [selectedRecurrence, setSelectedRecurrence] = useState(null);
    const [recurrences, setRecurrences] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // États pour la CRÉATION
    const [showModal, setShowModal] = useState(false);
    const [showMonthsModal, setShowMonthsModal] = useState(false);
    const [currentRecurrenceData, setCurrentRecurrenceData] = useState(null);

    // États pour la modification de factures et opérations
    const [showFactureModal, setShowFactureModal] = useState(false);
    const [editingFacture, setEditingFacture] = useState(null);
    const [showOperationModal, setShowOperationModal] = useState(false);
    const [editingOperation, setEditingOperation] = useState(null);

    // État pour gérer l'affichage de la gestion des dettes/créances
    const [showDebtCreditFields, setShowDebtCreditFields] = useState(false);

    // 1. Créez une référence pour stocker la dernière récurrence sélectionnée
    const selectedRecurrenceRef = useRef(null);

    // 2. Mettez à jour cette référence à chaque fois que l'état change
    useEffect(() => {
        selectedRecurrenceRef.current = selectedRecurrence;
    }, [selectedRecurrence]);

    // Filtrer les personnes gérées (is_managed = true)
    const managedPersons = persons.filter(p => p.is_managed === true || p.is_managed === 1);

    // Combinaison des comptes et des personnes pour le sélecteur
    const accountsWithPersonName = accounts.map((account) => ({
        ...account,
        personName: persons.find((p) => p.id === account.personId)?.name || "",
    }));

    const fetchRecurrences = useCallback(async () => {
        try {
            const response = await authFetch.get(
                `/recurrences`
            );
            if (!response.ok) {
                throw new Error("Erreur de récupération des récurrences");
            }
            const data = await response.json();
            const uniqueData = Array.from(new Map(data.map(item => [item.id, item])).values());
        
            setRecurrences(uniqueData);

            const currentSelectedId = selectedRecurrenceRef.current ? selectedRecurrenceRef.current.id : null;

            if (currentSelectedId) {
                // Nous trouvons le même ID dans la nouvelle liste (data)
                const updatedSelected = data.find(r => r.id === currentSelectedId);
                
                // Si l'élément existe, on met à jour l'état pour forcer le re-render
                if (updatedSelected) {
                    setSelectedRecurrence(updatedSelected);
                }
            }
        } catch (error) {
            console.error("Erreur lors du chargement des récurrences :", error);
            notify("Impossible de charger les récurrences", "error");
        }
    }, [notify]);

    // Fonction de callback à passer aux modales
    const handleTransactionDeleted = useCallback(() => {
        // 1. Déclenche un fetch direct de toutes les récurrences
        fetchRecurrences();
        // 2. Déclenche le rafraîchissement du Dashboard (pour le solde)
        refreshDashboardData(); 
        notify("Transaction supprimée et mois récurrent mis à jour.", 'success');
    }, [fetchRecurrences, refreshDashboardData, notify]);

    // ✅ Fonction pour déclencher la vérification des transactions manquées/futures
    const runRecurrenceCheck = useCallback(async () => {
        if (!currentUser) return;
        try {
            const message = await processRecurrences();
            notify(`✅ Vérification des transactions manquées terminée: ${message}`);
            // Après le backfill, nous rafraîchissons les récurrences pour voir si des transactions liées apparaissent
            fetchRecurrences(); 
        } catch (error) {
            notify(`⚠️ Échec de la vérification de backfill: ${error.message}`, 'error');
        }
    }, [currentUser, fetchRecurrences, notify]);

    useEffect(() => {
        fetchRecurrences();
    }, [currentUser, fetchRecurrences]);


    // Logique de sélection d'une récurrence (pour MODIFICATION)
    const handleRecurrenceSelect = (recurrence) => {
    setSelectedRecurrence(recurrence);
    setSelectedYear(new Date().getFullYear());

    setShowDebtCreditFields(false);
    };

    // Logique d'ouverture du modal de CRÉATION
    const handleNewRecurrence = () => {
        setSelectedRecurrence(null);
        setCurrentRecurrenceData(null);
        
        // 💡 MISE À JOUR : Créer les données d'initialisation
        const initialData = {
            // Ces valeurs seront lues dans RecurrenceModal.jsx
            initialPersonId: selectedPerson?.id, 
            initialAccountId: selectedAccount?.id,
        };

        setCurrentRecurrenceData(initialData); // Enregistre les IDs par défaut
        setShowModal(true);
    };

    // Logique pour passer de la première étape à la deuxième (modal des mois)
    const handleNextStep = (data) => {
        setCurrentRecurrenceData(data);
        setShowModal(false);
        setShowMonthsModal(true);
    };

    // Fonction pour la sauvegarde finale (appel API)
    const handleRecurrenceSaved = async (finalData) => {
        try {
            await saveRecurrence(finalData); // Utilise le service commun

            setShowMonthsModal(false);
            await runRecurrenceCheck();

            notify(`Récurrence ${finalData.id ? 'modifiée' : 'créée'} avec succès !`);

        } catch (error) {
            console.error("❌ Erreur de sauvegarde de récurrence:", error);
            notify(`Erreur lors de la création de la récurrence : ${error.message}`);
            setShowMonthsModal(false);
        }
    };

    const handleViewTransaction = async (transaction) => {
        if (!transaction || !transaction.id) return;

        const type = transaction.transaction_type; // 'Facture' ou 'Opération'

        try {
            // ✅ 1. Récupère les données complètes (y compris la date valide)
            const fullTransactionData = await fetchTransactionById(type, transaction.id);

            if (type === 'Facture') {
                setEditingFacture(fullTransactionData); // Maintenant, c'est l'objet complet
                setShowFactureModal(true);

            } else if (type === 'Opération') {
                setEditingOperation(fullTransactionData); // Maintenant, c'est l'objet complet
                setShowOperationModal(true);
            }
        } catch (error) {
            console.error(`❌ Erreur lors du chargement de la transaction ${type}:`, error);
            notify(`Impossible de charger la transaction : ${error.message}`);
        }
    };

    // Fonctions de fermeture (pour les callbacks onSave/onCancel des modales)
    const handleCloseFactureModal = () => {
        setShowFactureModal(false);
        setEditingFacture(null);
        // Après modification, il faut rafraîchir le Dashboard
        refreshDashboardData();
    };

    const handleCloseOperationModal = () => {
        setShowOperationModal(false);
        setEditingOperation(null);
        // Après modification, il faut rafraîchir le Dashboard
        refreshDashboardData();
    };

    // ✅ Logique de cocher/décocher les mois dans le panneau de droite (MODIFICATION)
    const handleMonthToggle = (monthNumber) => {
        if (!selectedRecurrence) return;

        const monthIndexInt = monthNumber;

        // 1. CAS DU CYCLE DE RÉFÉRENCE (Modification du Défaut pour les années futures)
        if (selectedYear === 'reference') {
            const initialMonths = selectedRecurrence?.initialMonthsCycle || [];
            const isCurrentlyActive = initialMonths.includes(monthIndexInt);

            // Mise à jour de l'état local initialMonthsCycle
            let newInitialMonths;
            if (isCurrentlyActive) {
                newInitialMonths = initialMonths.filter(m => m !== monthIndexInt);
            } else {
                newInitialMonths = [...initialMonths, monthIndexInt].sort((a, b) => a - b);
            }

            // Mettre à jour l'état de la récurrence sélectionnée (pour l'affichage immédiat)
            setSelectedRecurrence(prev => ({
                ...prev,
                // Assurez-vous que le champ est nommé 'initialMonthsCycle' dans l'état local
                initialMonthsCycle: newInitialMonths,
            }));

            // APPEL IMMÉDIAT À LA SAUVEGARDE ASYNCHRONE du cycle par défaut
            saveInitialMonthsCycle(newInitialMonths, selectedRecurrence.id);

            return; // FIN : Rien d'autre à faire pour la référence
        }

        // 2. CAS D'UNE ANNÉE SPÉCIFIQUE (Exception/Création immédiate)

        const recurrenceId = selectedRecurrence.id;
        const year = selectedYear;
        const day = selectedRecurrence.day;

        // Vérifier si une transaction existe déjà pour l'année/mois
        const transactionExists = getTransactionForMonth(monthNumber, year);

        // Déterminer l'état actuel du mois (en ignorant transactionExists pour l'isActive)
        const { isActive: isCurrentlyActive } = getMonthStatusInfo(monthNumber, year);

        const newStatus = !isCurrentlyActive; // Le nouvel état souhaité

        // 💡 Nouvelle vérification : Empêcher le DÉCOCHAGE si une transaction est TRAITÉE.
        if (!newStatus && transactionExists) {
            notify("Impossible de désactiver un mois pour lequel une transaction a déjà été créée. Supprimez d'abord la transaction associée ou modifiez-la.");
            return;
        }

        // VÉRIFICATION DE LA DATE DU JOUR pour la création immédiate (seulement si on ACTIVE)
        const today = new Date();
        const targetDate = new Date(year, monthNumber - 1, day);
        const isFutureTransaction = targetDate.getTime() > today.getTime();

        // 1. Appel à l'API pour enregistrer l'exception (month, year, newStatus)
        handleToggleMonthException(recurrenceId, monthNumber, year, newStatus);

        // 2. Création de transaction immédiate (si ACTIVATION et PASSÉ/PRÉSENT)
        if (newStatus === true && !isFutureTransaction) {
            handleCreateTransactionNow(recurrenceId, monthNumber, year);
        } else if (newStatus === true && isFutureTransaction) {
            notify("Le mois a été activé. La transaction sera générée automatiquement le ou après la date du " + targetDate.toLocaleDateString('fr-FR') + ".");
        }
    };

    // ✅ Déclenche la création immédiate de la transaction
    const handleCreateTransactionNow = async (recurrenceId, monthNumber, year) => {
        try {
            const response = await authFetch.post('/recurrences/create-now', {
                recurrenceId,
                month: monthNumber,
                year: year
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Échec de la création immédiate de la transaction.');
            }

            const data = await response.json();
            notify(`Transaction créée : ${data.message}`);
            // Une fois la transaction créée, nous rechargeons les données
            fetchRecurrences();

        } catch (error) {
            console.error("❌ Erreur de création immédiate de transaction:", error);
            notify(`Erreur lors de la création de la transaction : ${error.message}`);
        }
    };

    // Ajoutez cette fonction (doit appeler la nouvelle route API)
    const handleToggleMonthException = async (recurrenceId, monthNumber, year, newStatus) => {
        try {
            // 🔑 SÉCURISATION : Utilisation de authFetch
            const response = await authFetch.post('/recurrence_months/toggle', {
                recurrenceId,
                monthNumber,
                year,
                isActive: newStatus
            });

            if (response.ok) {
                // ✅ NOUVEAU : Mise à jour immédiate de l'état `recurrences`
                setRecurrences(prevRecurrences => {
                    // 1. Trouver l'index de la récurrence modifiée
                    const recurrenceIndex = prevRecurrences.findIndex(r => r.id === recurrenceId);
                    if (recurrenceIndex === -1) return prevRecurrences;

                    // 2. Créer une copie de la liste des récurrences
                    const newRecurrences = [...prevRecurrences];
                    // 3. Cloner l'objet de récurrence à modifier
                    const updatedRecurrence = { ...newRecurrences[recurrenceIndex] };

                    // 4. Initialiser monthExceptions si nécessaire
                    if (!updatedRecurrence.monthExceptions) {
                        updatedRecurrence.monthExceptions = {};
                    }
                    if (!updatedRecurrence.monthExceptions[year]) {
                        updatedRecurrence.monthExceptions[year] = {};
                    }

                    // 5. Appliquer la modification du statut du mois
                    updatedRecurrence.monthExceptions[year][monthNumber] = newStatus;

                    // 6. Remplacer l'ancienne récurrence par la nouvelle
                    newRecurrences[recurrenceIndex] = updatedRecurrence;

                    return newRecurrences;
                });

                // L'état de l'interface va se mettre à jour automatiquement grâce à cette modification.

            } else {
                console.error("Échec de la mise à jour du mois récurrent.");
            }
        } catch (error) {
            console.error("Erreur lors de la bascule du mois:", error);
        }
    };

    // Logique de modification dans le formulaire de gauche (MODIFICATION/SUPPRESSION)
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        // Conversion numérique pour certains champs
        const numericValue = ["day", "montant", "categorie_id", "sourceAccountId", "destinationAccountId", "person_id", "account_id"].includes(name) ? Number(value) : value;

        setSelectedRecurrence((prev) => ({
            ...prev,
            [name]: numericValue,
        }));
    };

    // ✅ Enregistre le cycle par défaut immédiatement
    const saveInitialMonthsCycle = async (newInitialMonths, recurrenceId) => {
        try {
            // ... (formatage de la date inchangé)
            let formattedStartDate = selectedRecurrence.startDate;
            if (formattedStartDate && formattedStartDate.includes('T')) {
                formattedStartDate = new Date(formattedStartDate).toISOString().split('T')[0];
            }

            // 🗑️ NETTOYAGE : Créer l'objet sans userId/user_id
            const dataToSaveRaw = { ...selectedRecurrence };
            delete dataToSaveRaw.user_id; 
            delete dataToSaveRaw.userId; 

            const dataToSave = {
                ...dataToSaveRaw,
                startDate: formattedStartDate,
                initial_months_cycle: JSON.stringify(newInitialMonths),
            };

            // 🔑 SÉCURISATION : Utilisation de authFetch
            const response = await authFetch.put(`/recurrences/${recurrenceId}`, dataToSave);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Échec de la mise à jour automatique du cycle.');
            }

            // 🚀 NOUVELLE LOGIQUE: Mise à jour manuelle de l'état local `recurrences`
            // et de `selectedRecurrence` SANS recharger toutes les données du serveur.

            // Mettre à jour la liste globale des récurrences (recurrences)
            setRecurrences(prevRecurrences => {
                const recurrenceIndex = prevRecurrences.findIndex(r => r.id === recurrenceId);
                if (recurrenceIndex === -1) return prevRecurrences;

                const newRecurrences = [...prevRecurrences];
                const updatedRecurrence = {
                    ...newRecurrences[recurrenceIndex],
                    initialMonthsCycle: newInitialMonths, // Mettre à jour le cycle dans l'objet
                };

                newRecurrences[recurrenceIndex] = updatedRecurrence;

                setSelectedRecurrence(updatedRecurrence);

                return newRecurrences;
            });

            // Suppression de l'appel fetchRecurrences() qui causait la réinitialisation

        } catch (error) {
            console.error("❌ Erreur lors de la sauvegarde automatique du cycle:", error);
            notify(`Erreur lors de la mise à jour automatique du cycle par défaut: ${error.message}`);
        }
    };

    // Logique de SAUVEGARDE (Modification)
    const handleSave = async () => {
        if (!selectedRecurrence?.id) return;

        // Définir isFacture localement
        const isFacture = selectedRecurrence.type === 'Facture';

        // Récupérer la récurrence actuelle (pour l'ancienne startDate)
        const originalRecurrence = recurrences.find(r => r.id === selectedRecurrence.id);
        const oldStartDate = originalRecurrence.startDate
            ? new Date(originalRecurrence.startDate).toISOString().split('T')[0]
            : null;

        const newStartDate = selectedRecurrence.startDate
            ? new Date(selectedRecurrence.startDate).toISOString().split('T')[0]
            : null;

        let proceedSave = true;
        let needsSync = false; // ✅ Indicateur si la synchronisation est nécessaire

        // 1. VÉRIFICATION DES TRANSACTIONS À SUPPRIMER SI LA DATE AVANCE
        if (needsSync) {
            // 🔑 SÉCURISATION : check-startdate
            try {
                const response = await authFetch.post(`/recurrences/check-startdate/${selectedRecurrence.id}`, newStartDate);

                if (!response.ok) throw new Error("Échec de la vérification de date.");

                const { affectedTransactions } = await response.json();

                if (affectedTransactions.length > 0) {
                    const affectedList = affectedTransactions.map(t =>
                        `[${t.date}] ${t.type} ID: ${t.id}`
                    ).join('\n');

                    const confirmationMessage =
                        `ATTENTION: La nouvelle date de début supprimera les transactions suivantes créées automatiquement avant cette date :\n\n` +
                        `${affectedList}\n\n` +
                        `Voulez-vous continuer la sauvegarde et supprimer ces transactions ?`;

                    if (!window.confirm(confirmationMessage)) {
                        proceedSave = false;
                    }
                }

            } catch (error) {
                console.error("Erreur de vérification de startDate:", error);
                notify("Erreur lors de la vérification des transactions affectées. Annulation de la sauvegarde.");
                proceedSave = false;
            }
        } else if (newStartDate && oldStartDate && newStartDate < oldStartDate) {
            needsSync = true; // Si la date recule, on a besoin d'une resynchronisation (pour créer les manquantes)
        }

        if (!proceedSave) return;

        // 🗑️ NETTOYAGE : Créer l'objet sans userId/user_id
        const dataToSaveRaw = { ...selectedRecurrence };
        delete dataToSaveRaw.user_id; 
        delete dataToSaveRaw.userId; 

        // 2. ENVOI DE LA SAUVEGARDE AU BACKEND (Mise à jour de la récurrence)
        const initialMonthsCycleToSave = selectedRecurrence.initialMonthsCycle || [];

        const dataToSave = {
            ...dataToSaveRaw,
            startDate: newStartDate, 
            initial_months_cycle: JSON.stringify(initialMonthsCycleToSave),
            payerPersonId: isFacture ? Number(selectedRecurrence.payerPersonId) || null : null,
            beneficiaryPersonId: isFacture ? Number(selectedRecurrence.beneficiaryPersonId) || null : null,
        };

        try {
            // 🔑 SÉCURISATION : PUT
            const response = await authFetch.put(`/recurrences/${selectedRecurrence.id}`, dataToSave);

            if (!response.ok) throw new Error('Échec de la modification');

            if (needsSync) {
                // 🔑 CORRECTION ICI : Passer un objet et non juste la string
                const syncResponse = await authFetch.post(`/recurrences/sync-startdate/${selectedRecurrence.id}`, { 
                    newStartDate: newStartDate 
                });

                if (!syncResponse.ok) {
                    throw new Error('Échec de la synchronisation des transactions associées.');
                }
            }

            // ... (Mise à jour de l'état local setRecurrences/setSelectedRecurrence)

            setRecurrences(prevRecurrences => {
                const recurrenceIndex = prevRecurrences.findIndex(r => r.id === selectedRecurrence.id);
                if (recurrenceIndex === -1) return prevRecurrences;

                const newRecurrences = [...prevRecurrences];
                const updatedRecurrence = {
                    ...newRecurrences[recurrenceIndex],
                    ...selectedRecurrence,
                    startDate: newStartDate, // Assurez-vous d'utiliser la date formatée
                };

                newRecurrences[recurrenceIndex] = updatedRecurrence;
                setSelectedRecurrence(updatedRecurrence);

                return newRecurrences;
            });

            notify("Récurrence modifiée avec succès !");
            // Recharger les récurrences pour obtenir les données de transactions mises à jour
            fetchRecurrences();

        } catch (error) {
            console.error("Erreur de sauvegarde:", error);
            notify("Erreur lors de la modification de la récurrence: " + error.message);
        }
    };

    // Logique de SUPPRESSION
    const handleDelete = async () => {
        if (!selectedRecurrence?.id || !window.confirm("Êtes-vous sûr de vouloir supprimer cette récurrence ? Cela supprimera toutes les transactions associées créées automatiquement.")) return;

        try {
            // 🔑 SÉCURISATION : DELETE
            const response = await authFetch.delete(`/recurrences/${selectedRecurrence.id}`);

            if (!response.ok) throw new Error('Échec de la suppression');

            setSelectedRecurrence(null);
            fetchRecurrences();

            notify("Récurrence supprimée avec succès !");

        } catch (error) {
            console.error("Erreur de suppression:", error);
            notify("Erreur lors de la suppression de la récurrence.");
        }
    };

    // Liste des mois et années (inchangé)
    const allMonths = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];

    const years = [
        new Date().getFullYear() - 1,
        new Date().getFullYear(),
        new Date().getFullYear() + 1,
    ];

    // Style pour la ligne cliquable
    const clickableRowStyle = (id) => ({
        ...styles.tableRow,
        // Mise à jour dynamique de la couleur de fond
        backgroundColor: selectedRecurrence?.id === id ? hoverColor : "transparent",
        // NOTE: Le hover est difficile à gérer ici, nous nous basons sur la sélection
    });

    // Logique pour basculer l'état Actif/Suspendu
    const handleActiveToggle = (e) => {
        setSelectedRecurrence((prev) => ({
            ...prev,
            is_active: e.target.checked, // Récupère directement l'état de la checkbox
        }));
    };

    // Rendu Conditionnel du Compte (pour le formulaire de modification de gauche)
    const renderAccountFields = () => {
        if (!selectedRecurrence) return null;

        // Détermine si le mode est Transfert
        const isTransfer = selectedRecurrence.sourceAccountId || selectedRecurrence.destinationAccountId;

        const isOperation = selectedRecurrence.type === 'Opération';

        const inputStyle = { ...styles.input, border: `1px solid ${themeColors.secondary}`, color: themeColors.text };
        const labelStyle = { ...styles.formLabel, color: themeColors.text };


        if (isTransfer) {
            // TRANSFERT: Compte Source et Destination
            return (
                <>
                    <label style={labelStyle}>Compte Source :</label>
                    <select name="sourceAccountId" value={selectedRecurrence.sourceAccountId || ''} onChange={handleFormChange} style={inputStyle} required>
                        <option value="">Sélectionner un compte</option>
                        {accountsWithPersonName.map((acc) => (
                            <option key={acc.id} value={acc.id}>{acc.personName} - {acc.name}</option>
                        ))}
                    </select>

                    <label style={labelStyle}>Compte d'arrivée :</label>
                    <select name="destinationAccountId" value={selectedRecurrence.destinationAccountId || ''} onChange={handleFormChange} style={inputStyle} required>
                        <option value="">Sélectionner un compte</option>
                        {accountsWithPersonName.filter(acc => acc.id !== selectedRecurrence.sourceAccountId).map((acc) => (
                            <option key={acc.id} value={acc.id}>{acc.personName} - {acc.name}</option>
                        ))}
                    </select>
                </>
            );
        } else if (selectedRecurrence.type === 'Facture' || isOperation) {
            // FACTURE ou OPÉRATION STANDARD : Compte/Personne standard
            return (
                <>
                    <select name="person_id" value={selectedRecurrence.person_id || ''} onChange={handleFormChange} style={inputStyle} required>
                        <option value="">Sélectionner une personne</option>
                        {managedPersons.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>

                    <select name="account_id" value={selectedRecurrence.account_id || ''} onChange={handleFormChange} style={inputStyle} required>
                        <option value="">Sélectionner un compte</option>
                        {accountsWithPersonName.filter(acc => acc.personId === Number(selectedRecurrence.person_id)).map((acc) => (
                            <option key={acc.id} value={acc.id}>{acc.name}</option>
                        ))}
                    </select>
                </>
            );
        }
        return null;
    };

    // Fonction pour déterminer si un mois est actif ou non, en tenant compte des exceptions
    const getMonthStatusInfo = (monthNumber, year) => {
        if (!selectedRecurrence) return { isActive: false, statusText: "N/A", transaction: null };

        // 1. CYCLE DE RÉFÉRENCE : Strictement visuel (pas de transactions)
        if (year === 'reference') {
            const initialMonths = selectedRecurrence?.initialMonthsCycle || [];
            const isActive = initialMonths.includes(monthNumber);
            return { 
                isActive, 
                statusText: isActive ? "Activé" : "Désactivé", 
                transaction: null 
            };
        }

        // 2. ANNÉES RÉELLES
        const numericYear = Number(year);
        const yearData = selectedRecurrence.monthExceptions?.[numericYear] || {};
        const linkedTx = yearData[`tx_${monthNumber}`]; 

        // Si lié à une transaction, on retourne l'objet pour afficher le bouton
        if (linkedTx && linkedTx.id) {
            return { 
                isActive: true, 
                statusText: "", // On vide le texte car le bouton sera affiché
                transaction: { id: linkedTx.id, transaction_type: linkedTx.type } 
            };
        }

        // Sinon, calcul du statut d'activation standard
        const startYear = new Date(selectedRecurrence.startDate).getFullYear();
        const startMonth = new Date(selectedRecurrence.startDate).getMonth() + 1;
        
        if (numericYear < startYear) return { isActive: false, statusText: "Inactif", transaction: null };

        let isActive = false;
        if (yearData.hasOwnProperty(monthNumber)) {
            isActive = yearData[monthNumber];
        } else {
            const isMonthInCycle = selectedRecurrence.initialMonthsCycle?.includes(monthNumber);
            isActive = (numericYear === startYear && monthNumber < startMonth) ? false : isMonthInCycle;
        }

        return { isActive, statusText: isActive ? "Activé" : "Désactivé", transaction: null };
    };

    // ✅ Carte de hachage des transactions traitées pour la récurrence sélectionnée
    const processedTransactionsMap = React.useMemo(() => {
        if (!selectedRecurrence?.processed_transactions) return new Map();

        const map = new Map(); 

        selectedRecurrence.processed_transactions.forEach(t => {
            const year = t.year;
            const month = t.month;

            if (!map.has(year)) {
                map.set(year, new Map()); 
            }
            map.get(year).set(month, t);
        });

        return map;
    }, [selectedRecurrence]); 


    // -------------------------------------------------------------
    // ✅ Vérification de l'existence de la transaction
    // -------------------------------------------------------------
    const getTransactionForMonth = useCallback((monthNumber, year) => {
        if (year === 'reference') {
            return undefined;
        }
        const targetYear = year === 'reference' ? new Date().getFullYear() : Number(year);
        return processedTransactionsMap.get(targetYear)?.get(monthNumber);

    }, [processedTransactionsMap]);
  
    return (
        <div style={styles.container}>
            {/* En-tête de la page */}
            <div style={styles.header}>
                <h1 style={{
                    ...styles.title,
                    color: themeColors.title,
                }}>
                    Gestion des récurrences</h1>
                <div style={styles.headerButtons}>
                    <button
                        style={{ ...styles.button, ...primaryButtonDynamic }}
                        onClick={handleNewRecurrence}
                    >
                        ➕ Nouvelle récurrence
                    </button>
                    <Link to="/dashboard">
                        <button style={{ ...styles.button, ...secondaryButtonDynamic }}>
                            Retour au Dashboard
                        </button>
                    </Link>
                </div>
            </div>

            <hr style={{ borderTop: `1px solid ${themeColors.secondary}50`, marginBottom: '0px' }} />

            {/* ✅ STRUCTURE CORRIGÉE : 3 COLONNES SÉPARÉES */}
            <div style={styles.mainContent}>

                {/* COLONNE 1 : Liste des récurrences */}
                <div style={{ 
                    ...styles.recurringListSection, 
                    backgroundColor: columnBg, 
                    border: `1px solid ${borderCol}` 
                }}>
                    <h2 style={{...styles.title, color: themeColors.text}}>Liste des récurrences</h2>
                    <table style={{...styles.table, backgroundColor: columnBg}}>
                        <thead>
                            <tr style={{ backgroundColor: themeColors.background }}>
                                <th style={{ ...styles.tableCell, position: 'sticky', top: 0, backgroundColor: themeColors.background, zIndex: 1, fontWeight: 'bold', borderBottom: `1px solid ${borderCol}` }}>Jour</th>
                                <th style={{ ...styles.tableCell, position: 'sticky', top: 0, backgroundColor: themeColors.background, zIndex: 1, fontWeight: 'bold', borderBottom: `1px solid ${borderCol}` }}>Description</th>
                                <th style={{ ...styles.tableCell, position: 'sticky', top: 0, backgroundColor: themeColors.background, zIndex: 1, fontWeight: 'bold', borderBottom: `1px solid ${borderCol}` }}>Montant</th>
                                <th style={{ ...styles.tableCell, position: 'sticky', top: 0, backgroundColor: themeColors.background, zIndex: 1, fontWeight: 'bold', borderBottom: `1px solid ${borderCol}` }}>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recurrences.map((r) => (
                                <tr
                                    key={r.id}
                                    onClick={() => handleRecurrenceSelect(r)}
                                    style={{
                                        ...clickableRowStyle(r.id),
                                        // Fond blanc si non sélectionné, couleur de survol si sélectionné
                                        backgroundColor: selectedRecurrence?.id === r.id ? hoverColor : columnBg,
                                    }}
                                >
                                    <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>{r.day}</td>
                                    <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>
                                        {r.description}
                                    </td>
                                    <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}`, color: Number(r.montant) >= 0 ? themeColors.success : themeColors.danger }}>{formatCurrency(r.montant)} €</td>
                                    <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>{r.type === 'Facture' ? 'Facture' : `Opération`}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* COLONNE 2 : Formulaire de modification/Suppression */}
                <div style={{...styles.leftPanel, backgroundColor: columnBg, border: `1px solid ${borderCol}`}}>
                    {selectedRecurrence ? (
                        <>
                            {/* Zone qui scrolle avec les champs */}
                            <div style={styles.formScrollArea}>
                                <h2 style={{...styles.title, color: themeColors.secondary}}>
                                    {selectedRecurrence ? "Modification de la récurrence" : "Sélectionner une récurrence dans la liste"}
                                </h2>
                                <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
                                    <select 
                                        name="type" 
                                        value={(selectedRecurrence.type === 'Standard' || selectedRecurrence.type === 'Transfert') ? 'Opération' : 'Facture'} 
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSelectedRecurrence(prev => ({
                                                ...prev,
                                                type: val === 'Opération' ? 'Standard' : 'Facture' // On force 'Standard' par défaut si c'est une opération
                                            }));
                                        }} 
                                        style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
                                    >
                                        <option value="Facture">Facture</option>
                                        <option value="Opération">Opération</option>
                                    </select>

                                    {selectedRecurrence.type === 'Opération' && (
                                        // ... (Sélecteur Standard/Transfert inchangé)
                                        <select
                                            // ... (logique du select inchangée)
                                            name="isTransfer"
                                            value={selectedRecurrence.sourceAccountId ? 'Transfert' : 'Standard'}
                                            onChange={(e) => {
                                                const isTransferring = e.target.value === 'Transfert';
                                                setSelectedRecurrence(prev => ({
                                                    ...prev,
                                                    sourceAccountId: isTransferring ? prev.sourceAccountId : null,
                                                    destinationAccountId: isTransferring ? prev.destinationAccountId : null,
                                                    account_id: isTransferring ? null : prev.account_id,
                                                    person_id: isTransferring ? null : prev.person_id,
                                                    // Lorsque l'on passe en Standard, on réinitialise les champs D/C (sécurité)
                                                    payerPersonId: isTransferring ? null : prev.payerPersonId, 
                                                    beneficiaryPersonId: isTransferring ? null : prev.beneficiaryPersonId,
                                                }));
                                            }}
                                            style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
                                        >
                                            <option value="Standard">Standard</option>
                                            <option value="Transfert">Transfert</option>
                                        </select>
                                    )}

                                    <input type="number" name="day" value={selectedRecurrence.day} onChange={handleFormChange} min="1" max="31" style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} required />

                                    <input type="number" name="montant" value={selectedRecurrence.montant} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} step="0.01" required />

                                    <select name="categorie_id" value={selectedRecurrence.categorie_id} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} required>
                                        <option value="">Sélectionner une catégorie</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>

                                    <input type="text" name="description" value={selectedRecurrence.description} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} required />

                                    <select name="paiementMode" value={selectedRecurrence.paiementMode} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}>
                                        {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
                                    </select>
                                    
                                    {/* ✅ GESTION DES COMPTES STANDARDS (Facture / Opération Standard) */}
                                    {selectedRecurrence.type !== 'Transfert' && (
                                        <>
                                            <select name="person_id" value={selectedRecurrence.person_id || ''} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} required>
                                                <option value="">Sélectionner une personne</option>
                                                {managedPersons.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>

                                            <select name="account_id" value={selectedRecurrence.account_id || ''} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }} required>
                                                <option value="">Sélectionner un compte</option>
                                                {accountsWithPersonName.filter(acc => acc.personId === Number(selectedRecurrence.person_id)).map((acc) => (
                                                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}

                                    {/* ✅ STATUT (Payé / Non Payé) */}
                                    <select name="statut" value={selectedRecurrence.statut} onChange={handleFormChange} style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}>
                                        <option>Payé</option>
                                        <option>Non payé</option>
                                    </select>
                                    
                                    {/* ✅ GESTION DES COMPTES TRANSFERTS */}
                                    {selectedRecurrence.type === 'Transfert' && renderAccountFields()}

                                    {/* ✅ LIGNE 1 : DATE DE DÉBUT et STATUT ACTIF */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                                        {/* Colonne 1: Date de début */}
                                        <div>
                                            <label style={styles.formLabel}>Date de début :</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={selectedRecurrence.startDate
                                                    ? new Date(selectedRecurrence.startDate).toISOString().split('T')[0]
                                                    : ''}
                                                onChange={handleFormChange}
                                                style={{ ...styles.input, border: `1px solid ${themeColors.secondary}`, width: '90%' }}
                                                required
                                            />
                                        </div>
                                        
                                        {/* Colonne 2: Statut Actif */}
                                        <div>
                                            <label style={styles.formLabel}>Statut Actif :</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '35px' }}>
                                                <input
                                                    type="checkbox"
                                                    name="is_active"
                                                    checked={selectedRecurrence.is_active || false}
                                                    onChange={handleActiveToggle}
                                                    style={{ width: 'auto' }}
                                                />
                                                <span style={{ fontWeight: 'bold', color: selectedRecurrence.is_active ? themeColors.success : themeColors.defaultButton }}>
                                                    {selectedRecurrence.is_active ? 'Actif' : 'Suspendu'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ✅ BLOC GESTION DETTES/CRÉANCES (Si Facture) */}
                                    {selectedRecurrence.type === 'Facture' && (
                                        <div style={{ border: `1px solid ${themeColors.secondary}20`, borderRadius: '4px', padding: '10px', marginTop: '10px' }}>
                                            
                                            <button 
                                                type="button"
                                                onClick={() => setShowDebtCreditFields(!showDebtCreditFields)}
                                                style={{ 
                                                    // Style de bouton simple pour le toggle
                                                    backgroundColor: themeColors.background, 
                                                    color: themeColors.text,
                                                    padding: '8px 12px',
                                                    border: `1px solid ${themeColors.inputBorder}`,
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    marginBottom: '10px',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {showDebtCreditFields ? '▼ Masquer' : '► Afficher'} Gestion Dettes/Créances
                                            </button>

                                            {showDebtCreditFields && (
                                                <div style={styles.form}>
                                                                                                
                                                    {/* Payeur (Créancier potentiel) */}
                                                    <select 
                                                        name="payerPersonId" 
                                                        value={selectedRecurrence.payerPersonId || ''} 
                                                        onChange={handleFormChange} 
                                                        style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
                                                        required
                                                    >
                                                        <option value="">Sélectionner une personne</option>
                                                        {/* Toutes les personnes (gérées et non gérées) */}
                                                        {persons.map((p) => (<option key={`payer-${p.id}`} value={p.id}>{p.name} (Paiement)</option>))}
                                                    </select>

                                                    {/* Bénéficiaire (Débiteur potentiel) */}
                                                    <select 
                                                        name="beneficiaryPersonId" 
                                                        value={selectedRecurrence.beneficiaryPersonId || ''} 
                                                        onChange={handleFormChange} 
                                                        style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
                                                        required
                                                    >
                                                        <option value="">Sélectionner une personne</option>
                                                        {/* Toutes les personnes (gérées et non gérées) */}
                                                        {persons.map((p) => (<option key={`benef-${p.id}`} value={p.id}>{p.name} (Bénéficiaire)</option>))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </form>
                            </div>

                            {/* Zone fixe en bas pour les boutons */}
                            <div style={{...styles.stickyActions, backgroundColor: columnBg }}>
                                <button type="button" onClick={handleSave} style={{ ...styles.button, ...saveButtonDynamic }}>
                                    Sauvegarder
                                </button>
                                <button type="button" onClick={handleDelete} style={{ ...styles.button, ...deleteButtonDynamic }}>
                                    Supprimer
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={{padding: '35vh 5vh', textAlign: 'center'}}>
                            <p>Veuillez sélectionner une récurrence dans la liste de gauche pour afficher ses détails.</p>
                        </div>
                    )}
                </div>

                {/* COLONNE 3 : Liste des mois */}
                <div style={{ 
                    ...styles.monthListSection, 
                    backgroundColor: columnBg, 
                    border: `1px solid ${borderCol}` 
                }}>
                    <h2 style={{...styles.title, color: themeColors.text}}>
                        Mois de la récurrence
                    </h2>
                    <div style={{ marginBottom: "10px" }}>
                        <label style={styles.formLabel}>Année/Référence : </label>
                        <select
                            value={selectedYear}
                            onChange={(e) => {
                                const newYear = e.target.value; 
                                setSelectedYear(newYear);
                            }}
                            style={{ ...styles.input, border: `1px solid ${themeColors.secondary}` }}
                            disabled={!selectedRecurrence}
                        >
                            <option value="reference">Cycle de Référence (Défaut)</option>
                            {years.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>
                    <table style={{ ...styles.table, backgroundColor: columnBg }}>
                        <thead>
                            <tr style={{ backgroundColor: themeColors.background }}>
                                <th style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>Mois</th>
                                <th style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>Activer</th>
                                <th style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allMonths.map((month, index) => {
                                const monthNumber = index + 1;

                                const { isActive, statusText, transaction } = getMonthStatusInfo(monthNumber, selectedYear);
    
                                const isReference = selectedYear === 'reference';
                                const hasTransaction = !!transaction;

                                return (
                                    <tr key={index}>
                                        <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>{month}</td>
                                        <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() => handleMonthToggle(monthNumber)}
                                                // On bloque la modif si une transaction existe déjà (sauf en mode référence)
                                                disabled={!selectedRecurrence || (hasTransaction && !isReference)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </td>
                                        <td style={{ ...styles.tableCell, borderBottom: `1px solid ${borderCol}` }}>
                                            {hasTransaction ? (
                                                /* SI TRANSACTION : On affiche UNIQUEMENT le bouton */
                                                <button
                                                    style={{ ...styles.button, ...primaryButtonDynamic, padding: '5px 10px', fontSize: '0.85em' }}
                                                    onClick={() => handleViewTransaction(transaction)}
                                                >
                                                    Voir {transaction.transaction_type === 'Facture' ? 'la facture' : "l'opération"}
                                                </button>
                                            ) : (
                                                /* SINON : On affiche le statut (Activé/Désactivé) */
                                                <span style={{ color: isActive ? themeColors.success : themeColors.defaultButton, fontWeight: 'bold' }}>
                                                    {statusText}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                
            </div>

      {/* Rendu des modales (à placer ici) */}
      {showFactureModal && (
        <FactureModal
          show={showFactureModal}
          editingFacture={editingFacture}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          selectedPerson={selectedPerson}
          selectedAccount={selectedAccount}
          refreshDashboardData={refreshDashboardData}
          onSave={handleCloseFactureModal}
          onCancel={handleCloseFactureModal}
          onDeleteCallback={handleTransactionDeleted}
        />
      )}
      {showOperationModal && (
        <OperationModal
          show={showOperationModal}
          editingOperation={editingOperation}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          selectedPerson={selectedPerson}
          selectedAccount={selectedAccount}
          refreshDashboardData={refreshDashboardData}
          onSave={handleCloseOperationModal}
          onCancel={handleCloseOperationModal}
          onDeleteCallback={handleTransactionDeleted}
        />
      )}

      {showModal && (
        <RecurrenceModal
          show={showModal}
          currentUser={currentUser}
          categories={categories}
          accounts={accounts}
          persons={persons}
          initialPersonId={currentRecurrenceData?.initialPersonId}
          initialAccountId={currentRecurrenceData?.initialAccountId}
          onClose={() => {
              setShowModal(false);
              setCurrentRecurrenceData(null); // Nettoyage de l'état
          }}
          onNextStep={handleNextStep}
          refreshAllData={refreshAllData}
        />
      )}

      {showMonthsModal && (
        <RecurrenceMonthsModal
          show={showMonthsModal}
          initialData={currentRecurrenceData}
          isEditing={false}
          onClose={() => setShowMonthsModal(false)}
          onSave={handleRecurrenceSaved}
        />
      )}

    </div>
  );
}